import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import { unifiedRBACService } from '@/services/unifiedRBACService';
import { iconService } from '@/services/iconService';
import api from '@/services/api';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CogIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  RocketLaunchIcon,
  PresentationChartLineIcon,
  StarIcon,
  ArrowPathIcon,
  Bars3Icon,
  DocumentTextIcon,
  ComputerDesktopIcon,
  HomeIcon,
  ArchiveBoxIcon,
  ShoppingBagIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { ResourcesWidget } from '../resources/ResourcesWidget';

/** Outline stroke + single theme color (aligned with provisioning template icon bar). */
const SIDEBAR_MONO_ICON =
  '!w-[18px] !h-[18px] flex-shrink-0 text-[var(--color-info)] [stroke:currentColor]';

/** One row component for both sidebar widths: labels hidden with `sr-only` when collapsed (same padding / min-height). */
const SIDEBAR_NAV_ROW_CLASS =
  'w-full min-h-[2.25rem] shrink-0 px-3 py-1.5 rounded text-sm flex items-center gap-3';

interface NavigationItem {
  id: string;
  display_name: string;
  route_path: string;
  icon: string;
  item_type: 'module' | 'screen' | 'node';
  sidebar_menu_visibility?: 'USER' | 'ADMIN' | 'NONE';
  is_admin?: boolean;
  translation_key?: string;
  is_featured?: boolean;
  quick_access?: boolean;
  usage_count?: number;
  last_accessed?: string;
  // Sorting fields for proper ordering
  parent_sort_order?: number;
  screen_sort_order?: number;
}

interface SidebarNavigationProps {
  isCollapsed: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  favorites: NavigationItem[];
  recentItems: NavigationItem[];
  onNavigate: (path: string) => void;
  // Mobile-specific props
  isMobile?: boolean;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
  // Desktop-specific props
  isDesktopOpen?: boolean;
}

export const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  isCollapsed,
  searchQuery,
  onSearchChange,
  favorites,
  recentItems,
  onNavigate,
  isMobile = false,
  isMobileOpen = false,
  onMobileClose,
  isDesktopOpen = false
}) => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { t, translationsLoading } = useLanguage();
  
  const [navigationLoading, setNavigationLoading] = useState(true);
  
  // Map database display_name to translation key (same as HorizontalNavigation)
  const getTranslationKey = (displayName: string, translationKey?: string): string => {
    // Use translation_key from database if available
    if (translationKey) {
      return translationKey;
    }
    
  // If no translation_key provided, return the display_name as fallback
  // This will show the raw display_name instead of a broken key
  console.warn(`No translation_key provided for display_name: ${displayName}`);
  return displayName;
  };

  // Memoized translation function to ensure re-rendering when language changes
  const getTranslatedText = useMemo(() => {
    return (displayName: string, translationKey?: string) => {
      if (translationsLoading) return displayName;
      const key = getTranslationKey(displayName, translationKey);
      const translatedText = t(key);
      return translatedText === key ? displayName : translatedText;
    };
  }, [translationsLoading, t]);
  
  const [showSearch, setShowSearch] = useState(false);
  const [searchResults, setSearchResults] = useState<NavigationItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchOverlayRef = useRef<HTMLDivElement>(null);

  // State for modules grouped by category
  const [businessModules, setBusinessModules] = useState<any[]>([]);
  const [adminModules, setAdminModules] = useState<any[]>([]);
  const [hasAdminDashboardPermission, setHasAdminDashboardPermission] = useState(false);

  // Track previous pathname to detect navigation away from admin pages
  const prevPathnameRef = useRef<string>(location.pathname);
  
  // Load modules grouped by category
  useEffect(() => {
    const loadModules = async () => {
      try {
        setNavigationLoading(true);
        
        // Load navigation tree once. Category-based fetching is deprecated.
        const navigationTree = await unifiedRBACService.getCurrentUserNavigation('BUSINESS');
        
        // Check if user has permission to ADMIN_DASHBOARD screen
        const checkAdminDashboardPermission = (tree: any): boolean => {
          // Recursively search through all modules and their children for ADMIN_DASHBOARD screen
          const findScreen = (modules: any[]): boolean => {
            for (const module of modules) {
              if (module.children) {
                for (const child of module.children) {
                  // Check if this is the ADMIN_DASHBOARD screen with permission
                  if (child.screen_code === 'ADMIN_DASHBOARD') {
                    const hasPermission = child.has_read_permission === true || 
                                         child.has_read_permission === 't' || 
                                         String(child.has_read_permission) === 'true';
                    return hasPermission;
                  }
                  // Check sub-modules recursively
                  if (child.module_code && child.children) {
                    const found = findScreen([child]);
                    if (found) return found;
                  }
                }
              }
            }
            return false;
          };
          return findScreen(tree.modules);
        };
        
        setHasAdminDashboardPermission(checkAdminDashboardPermission(navigationTree));
        
        // Build BUSINESS screens from screen-level menu visibility.
        // Screens under admin module branches are never shown in business sidebar.
        const buildBusinessScreens = (tree: any) => {
          const businessScreens: any[] = [];

          const collectBusinessScreens = (module: any, inheritedSortOrder?: number, inAdminBranch: boolean = false) => {
            if (!module) return;
            const baseSortOrder = inheritedSortOrder ?? (Number(module.sort_order) || 0);
            const moduleIsAdmin = module.is_admin === true || module.is_admin === 't';
            const insideAdminBranch = inAdminBranch || moduleIsAdmin;

            if (!Array.isArray(module.children)) return;

            const screens = module.children
              .filter((child: any) => child && child.screen_code)
              .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));

            screens.forEach((screen: any) => {
              const hasPermission =
                screen.has_read_permission === true ||
                screen.has_read_permission === 't' ||
                String(screen.has_read_permission) === 'true';
              const visibility = (screen.sidebar_menu_visibility || 'NONE').toUpperCase();
              if (!hasPermission || visibility !== 'USER' || insideAdminBranch) return;

              businessScreens.push({
                ...screen,
                item_type: 'screen',
                parent_module_sort_order: baseSortOrder
              });
            });

            const subModules = module.children
              .filter((child: any) => child && child.module_code && !child.screen_code)
              .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
            subModules.forEach((subModule: any) => collectBusinessScreens(subModule, baseSortOrder, insideAdminBranch));
          };

          [...(tree.modules || [])]
            .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0))
            .forEach((module: any) => {
              if (!module.parent_module_id) {
                collectBusinessScreens(module);
              }
            });

          businessScreens.sort((a: any, b: any) => {
            const aOrder = a.parent_module_sort_order || 0;
            const bOrder = b.parent_module_sort_order || 0;
            if (aOrder !== bOrder) return aOrder - bOrder;
            return (a.sort_order || 0) - (b.sort_order || 0);
          });

          return businessScreens;
        };

        // Build admin section using root modules explicitly marked as admin.
        const buildAdminRootModules = (tree: any) => {
          return [...(tree.modules || [])]
            .filter((module: any) => {
              const isRoot = !module.parent_module_id;
              const isAdmin = module.is_admin === true || module.is_admin === 't';
              return isRoot && isAdmin;
            })
            .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0))
            .map((module: any) => ({
              id: module.id,
              module_code: module.module_code,
              display_name: module.display_name,
              translation_key: module.translation_key,
              icon: module.icon,
              route_path: `/modules/${String(module.module_code || '').toLowerCase().replace(/_/g, '-')}`,
              sort_order: Number(module.sort_order) || 0,
              item_type: 'module',
              is_admin: true
            }));
        };

        setBusinessModules(buildBusinessScreens(navigationTree));
        setAdminModules(buildAdminRootModules(navigationTree));
      } catch (error) {
        console.error('Failed to load modules:', error);
        setBusinessModules([]);
        setAdminModules([]);
        setHasAdminDashboardPermission(false);
      } finally {
        setNavigationLoading(false);
      }
    };

    const wasOnAdminPage = prevPathnameRef.current.includes('/admin/navigation');
    const isOnAdminPage = location.pathname.includes('/admin/navigation');
    
    // Always load on pathname change
    loadModules();
    
    // If navigating away from admin navigation page, reload after a short delay
    // This ensures sidebar refreshes after editing modules
    if (wasOnAdminPage && !isOnAdminPage) {
      const timeoutId = setTimeout(() => {
        loadModules();
      }, 1000); // 1 second delay to ensure database updates are committed
      prevPathnameRef.current = location.pathname;
      return () => clearTimeout(timeoutId);
    }
    
    prevPathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Track navigation usage
  const trackNavigation = async (item: NavigationItem, action: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.warn('No authentication token available for navigation tracking');
        return;
      }

      // Check if user is authenticated before making API call
      if (!isAuthenticated) {
        console.warn('User not authenticated, skipping navigation tracking');
        return;
      }

      await api.post('/navigation/track', {
        [`navigation_${item.item_type}_id`]: item.id,
        action_type: action,
        session_id: sessionStorage.getItem('sessionId')
      });
    } catch (error) {
      console.error('Failed to track navigation:', error);
    }
  };

  // Handle search
  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Load navigation tree once. Category-based fetching is deprecated.
      const navigationTree = await unifiedRBACService.getCurrentUserNavigation('BUSINESS');
      
      const results: NavigationItem[] = [];
      const searchQuery = query.toLowerCase();
      
      // Recursive function to search through all modules and their children (including sub-modules)
      // Only returns screens, not modules or directories
      const searchModuleRecursively = (module: any, parentSortOrder?: number) => {
        const effectiveSortOrder = parentSortOrder !== undefined ? parentSortOrder : (module.sort_order || 0);
        
        // Search through all children (screens and sub-modules)
        if (module.children && Array.isArray(module.children)) {
          // Separate screens from sub-modules
          const screens = module.children.filter((child: any) => 
            child && child.screen_code !== undefined && child.screen_code !== null
          );
          const subModules = module.children.filter((child: any) => 
            child && child.module_code !== undefined && child.module_code !== null && !child.screen_code
          );
          
          // Search screens only (exclude modules/directories)
          const sortedScreens = screens
            .filter((screen: any) => screen && screen.display_name && screen.has_read_permission && !(screen.route_path || '').includes(':'))
            .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
          
          for (const screen of sortedScreens) {
            const displayName = screen.display_name.toLowerCase();
            const routePath = screen.route_path ? screen.route_path.toLowerCase() : '';
            
            if (displayName.includes(searchQuery) || routePath.includes(searchQuery)) {
              results.push({
                id: screen.id,
                display_name: screen.display_name,
                route_path: screen.route_path || '',
                item_type: 'screen',
                icon: screen.icon || 'DocumentTextIcon',
                translation_key: screen.translation_key,
                // Store parent module sort_order so screens inherit parent's order
                parent_sort_order: effectiveSortOrder,
                screen_sort_order: screen.sort_order || 0
              });
            }
          }
          
          // Recursively search sub-modules (important for admin modules with nested structure)
          for (const subModule of subModules) {
            searchModuleRecursively(subModule, effectiveSortOrder);
          }
        }
      };
      
      // Search modules
      // Sort modules by sort_order first to ensure proper ordering
      const allModules = [...(navigationTree.modules || [])]
        .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
      
      // Search through all modules recursively (including admin modules and their sub-modules)
      for (const module of allModules) {
        searchModuleRecursively(module);
      }
      
      // Sort results: only screens (modules/directories are excluded)
      // Sort by parent sort_order first, then screen sort_order
      results.sort((a: any, b: any) => {
        // Both should be screens, but handle edge cases
        if (a.item_type === 'screen' && b.item_type === 'screen') {
          const parentDiff = (a.parent_sort_order || 0) - (b.parent_sort_order || 0);
          if (parentDiff !== 0) return parentDiff;
          return (a.screen_sort_order || 0) - (b.screen_sort_order || 0);
        }
        
        return 0;
      });
      
      setSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Handle click outside search overlay
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchOverlayRef.current && !searchOverlayRef.current.contains(event.target as Node)) {
        setShowSearch(false);
      }
    };

    if (showSearch) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearch]);

  const getBusinessItemPath = (item: any): string => {
    const isModule = item.module_code !== undefined;
    if (!isModule) return item.route_path || '#';
    const moduleCode = item.module_code.toLowerCase().replace(/_/g, '-');
    if (item.parent_module_path) {
      return `/modules/${item.parent_module_path}/${moduleCode}`;
    }
    if (item.parent_module_code) {
      const parentCode = item.parent_module_code.toLowerCase().replace(/_/g, '-');
      return `/modules/${parentCode}/${moduleCode}`;
    }
    return `/modules/${moduleCode}`;
  };

  const getIconComponent = (iconName: string) => {
    const isEmoji = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u.test(iconName);

    if (iconName && isEmoji) {
      const emojiToIcon: Record<string, React.ComponentType<{ className?: string }>> = {
        '🛡️': ShieldCheckIcon,
        '🚨': ExclamationTriangleIcon,
        '✅': CheckCircleIcon,
        '🚀': RocketLaunchIcon,
        '📄': PresentationChartLineIcon,
        '🔍': MagnifyingGlassIcon,
        '⭐': StarIcon,
        '🕒': ArrowPathIcon,
        '📋': Bars3Icon,
        '❌': XMarkIcon,
        '🖥️': ComputerDesktopIcon,
        '🖥': ComputerDesktopIcon,
        // Resources / marketplace / discussions (legacy 20251213 emoji icons → outline, same as transformations discussions)
        '📦': ArchiveBoxIcon,
        '📚': BookOpenIcon,
        '🛒': ShoppingBagIcon,
        '🛍️': ShoppingBagIcon,
        '💬': ChatBubbleLeftRightIcon,
      };
      const Mapped = emojiToIcon[iconName];
      if (Mapped) {
        return <Mapped className={SIDEBAR_MONO_ICON} />;
      }
      return <span className="text-lg opacity-90">{iconName}</span>;
    }

    const legacyKeyToIcon: Record<string, string> = {
      search: 'magnifying-glass',
      star: 'star',
      clock: 'arrow-path',
      'bars-3': 'bars-3',
    };
    if (iconName && legacyKeyToIcon[iconName]) {
      const IconComponent = iconService.getIcon(legacyKeyToIcon[iconName]);
      return <IconComponent className={SIDEBAR_MONO_ICON} />;
    }
    if (iconName === 'x-mark') {
      return <XMarkIcon className={SIDEBAR_MONO_ICON} />;
    }

    if (iconName) {
      const IconComponent = iconService.getIcon(iconName);
      return <IconComponent className={SIDEBAR_MONO_ICON} />;
    }

    return <DocumentTextIcon className={SIDEBAR_MONO_ICON} />;
  };

  // Check if item is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Handle navigation click
  const handleNavigationClick = (item: NavigationItem) => {
    console.log('[SidebarNavigation] handleNavigationClick called:', {
      id: item.id,
      display_name: item.display_name,
      item_type: item.item_type,
      route_path: item.route_path,
      module_code: (item as any).module_code,
      screen_code: (item as any).screen_code,
      fullItem: item
    });
    trackNavigation(item, 'click');
    onNavigate(item.route_path);
    // Close mobile menu after navigation
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={onMobileClose}
        />
      )}
      
      {/* Sidebar Container */}
      <aside className={`
        bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] transition-all duration-300
        flex flex-col
        ${isCollapsed ? 'w-14' : 'w-64'}
        ${isMobile ? 'fixed top-0 inset-y-0 left-0 z-40 transform transition-transform duration-300 h-screen' : 'fixed top-[56px] left-0 z-30 h-[calc(100vh-56px)]'}
        ${isMobile && !isMobileOpen ? '-translate-x-full' : ''}
        ${!isMobile && !isDesktopOpen ? 'hidden' : ''}
        ${isMobile ? 'md:hidden' : 'hidden md:block'}
      `}>
      {/* Header - Fixed (collapsed: invisible tenant line preserves row height for alignment below) */}
      <div className="p-2 flex-shrink-0">
        <div className="flex min-h-[1.75rem] items-center justify-between gap-2">
          {!isCollapsed ? (
            <Link
              to="/dashboard"
              className="min-w-0 flex flex-1 items-center gap-2 truncate text-lg font-semibold text-[var(--color-primary-text)] transition-colors hover:text-blue-300"
            >
              <HomeIcon
                className={`${SIDEBAR_MONO_ICON} !w-5 !h-5 flex-shrink-0`}
                aria-hidden
              />
              <span className="min-w-0 truncate">
                {user?.tenantName || t('navigation.sidebar.loadingTenant')}
              </span>
            </Link>
          ) : (
            <>
              <span className="sr-only">
                {user?.tenantName
                  ? t('navigation.sidebar.homeWithTenant', { tenant: user.tenantName })
                  : t('navigation.sidebar.home')}
              </span>
              <Link
                to="/dashboard"
                className="flex min-h-[1.75rem] min-w-0 flex-1 items-center justify-center rounded text-[var(--color-primary-text)] transition-colors hover:text-blue-300"
                aria-label={
                  user?.tenantName
                    ? t('navigation.sidebar.homeWithTenant', { tenant: user.tenantName })
                    : t('navigation.sidebar.home')
                }
              >
                <HomeIcon className={`${SIDEBAR_MONO_ICON} !w-5 !h-5`} />
              </Link>
            </>
          )}
          {isMobile && onMobileClose && (
            <button
              onClick={onMobileClose}
              className="p-1 rounded hover:bg-[var(--color-secondary-bg)] transition-colors z-50 relative"
              aria-label={t('navigation.sidebar.closeSidebar')}
            >
              <XMarkIcon className="!w-[18px] !h-[18px]" />
            </button>
          )}
        </div>
      </div>

      {/* Search - Fixed */}
      {!isCollapsed && (
        <div className="relative px-2 py-2 flex-shrink-0">
          <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="!w-[18px] !h-[18px] text-[var(--color-secondary-text)]" />
          </div>
          <input
            type="text"
            placeholder={t('navigation.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-11 pr-4 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] rounded-md border border-[var(--color-border)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
          />
          {isSearching && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--color-accent-bg)]"></div>
            </div>
          )}
        </div>
      )}
      
      {isCollapsed && (
        <div className="px-0 py-0">
          <button
            type="button"
            onClick={() => setShowSearch(!showSearch)}
            className={cn(
              SIDEBAR_NAV_ROW_CLASS,
              'justify-center transition-colors',
              showSearch ? 'bg-[var(--color-secondary-bg)]' : 'hover:bg-[var(--color-secondary-bg)]'
            )}
            title={t('navigation.searchPlaceholder')}
          >
            <MagnifyingGlassIcon className="h-[18px] w-[18px] text-[var(--color-secondary-text)] [stroke:currentColor]" />
          </button>
        </div>
      )}

      {/* Collapsed Search Overlay */}
      {isCollapsed && showSearch && (
        <div ref={searchOverlayRef} className="absolute left-16 top-16 z-50 w-80 bg-[var(--color-primary-bg)] border border-[var(--color-border)] rounded-md shadow-lg">
          <div className="p-0">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="!w-[18px] !h-[18px] text-[var(--color-secondary-text)]" />
              </div>
              <input
                type="text"
                placeholder={t('navigation.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-11 pr-10 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] rounded-md border border-[var(--color-border)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                autoFocus
              />
              <button
                onClick={() => setShowSearch(false)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center p-1 rounded hover:bg-[var(--color-secondary-bg)] transition-colors"
                title={t('common.close')}
              >
                <XMarkIcon className="!w-[18px] !h-[18px]" />
              </button>
              {isSearching && (
                <div className="absolute inset-y-0 right-8 pr-3 flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--color-accent-bg)]"></div>
                </div>
              )}
            </div>
            
            {/* Search Results in Collapsed Mode */}
            {searchQuery && searchResults.length > 0 && (
              <div className="mt-0 max-h-64 overflow-y-auto">
                <h3 className="text-sm font-medium text-[var(--color-secondary-text)] mb-0">
                  {t('navigation.searchResults')}
                </h3>
                <div className="space-y-1">
                  {searchResults.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        handleNavigationClick(item);
                        setShowSearch(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center gap-3 transition-colors ${
                        isActive(item.route_path) 
                          ? 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]' 
                          : 'text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)]'
                      }`}
                    >
                      {item.icon && iconService.isIconAvailable(item.icon) ? (
                        React.createElement(iconService.getIcon(item.icon), { className: SIDEBAR_MONO_ICON })
                      ) : (
                        <div className="w-[18px] h-[18px] flex-shrink-0 flex items-center justify-center">{getIconComponent(item.icon)}</div>
                      )}
                      <span className="truncate text-sm flex-1 min-w-0 leading-tight">
                        {getTranslatedText(item.display_name, item.translation_key)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search Results */}
      {searchQuery && searchResults.length > 0 && !isCollapsed && (
        <div className="p-2 border-b border-[var(--color-border)]">
          <h3 className="text-sm font-medium text-[var(--color-secondary-text)] mb-1">
            {t('navigation.searchResults')}
          </h3>
          <div className="space-y-1">
            {searchResults.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigationClick(item)}
                className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-3 transition-colors ${
                  isActive(item.route_path) 
                    ? 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]' 
                    : 'text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)]'
                }`}
              >
                {item.icon && iconService.isIconAvailable(item.icon) ? (
                  React.createElement(iconService.getIcon(item.icon), { className: SIDEBAR_MONO_ICON })
                ) : (
                  <div className="w-[18px] h-[18px] flex-shrink-0 flex items-center justify-center">{getIconComponent(item.icon)}</div>
                )}
                <span className="truncate text-sm flex-1 min-w-0 leading-tight">
                  {translationsLoading ? item.display_name : (() => {
                    return getTranslatedText(item.display_name, item.translation_key);
                  })()}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}


      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
      {/* Main navigation: one row markup for expanded + collapsed (label sr-only when collapsed) */}
      {!navigationLoading && (businessModules.length > 0 || adminModules.length > 0) && (
        <div className="p-2 border-b border-[var(--color-border)]">
          <div className="space-y-0.5">
            {businessModules.length > 0 && (
              <div className="space-y-0.5">
                {businessModules.map((item: any) => {
                  const isModule = item.module_code !== undefined;
                  const itemPath = getBusinessItemPath(item);

                  if (
                    item.display_name?.toLowerCase().includes('template') ||
                    item.module_code?.toLowerCase().includes('template') ||
                    item.screen_code?.toLowerCase().includes('template')
                  ) {
                    console.log('[SidebarNavigation] Templates item:', {
                      id: item.id,
                      display_name: item.display_name,
                      module_code: item.module_code,
                      screen_code: item.screen_code,
                      route_path: item.route_path,
                      isModule,
                      itemPath,
                      parent_module_path: item.parent_module_path,
                      parent_module_code: item.parent_module_code,
                      item_type: item.item_type,
                      fullItem: item,
                    });
                  }

                  const isNavActive =
                    location.pathname === itemPath || location.pathname.startsWith(itemPath + '/');
                  const grayedOut = item.lacks_capability === true;
                  const label = getTranslatedText(item.display_name, item.translation_key);

                  const iconEl =
                    item.icon && iconService.isIconAvailable(item.icon) ? (
                      React.createElement(iconService.getIcon(item.icon), { className: SIDEBAR_MONO_ICON })
                    ) : (
                      <div className="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center">
                        {getIconComponent(item.icon || 'DocumentTextIcon')}
                      </div>
                    );

                  if (grayedOut) {
                    return (
                      <span
                        key={item.id}
                        title={t('navigation.upgradePlanTooltip')}
                        className={cn(
                          SIDEBAR_NAV_ROW_CLASS,
                          'cursor-not-allowed opacity-60 text-gray-500'
                        )}
                      >
                        {iconEl}
                        <span
                          className={cn(
                            'min-w-0 flex-1 truncate text-sm leading-tight',
                            isCollapsed && 'sr-only'
                          )}
                        >
                          {label}
                        </span>
                      </span>
                    );
                  }

                  return (
                    <Link
                      key={item.id}
                      to={itemPath}
                      title={isCollapsed ? label : undefined}
                      onClick={() => {
                        console.log('[SidebarNavigation] Link clicked:', {
                          display_name: item.display_name,
                          itemPath,
                          isModule,
                          module_code: item.module_code,
                          screen_code: item.screen_code,
                          route_path: item.route_path,
                        });
                        if (
                          item.screen_code === 'TEMPLATE_MANAGEMENT' ||
                          item.module_code === 'TEMPLATES' ||
                          item.display_name?.toLowerCase().includes('template')
                        ) {
                          console.log('[SidebarNavigation] Templates item click:', {
                            itemPath,
                            willNavigateTo: itemPath,
                          });
                        }
                      }}
                      className={cn(
                        SIDEBAR_NAV_ROW_CLASS,
                        'transition-colors',
                        isNavActive
                          ? 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]'
                          : 'text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)]'
                      )}
                    >
                      {iconEl}
                      <span
                        className={cn(
                          'min-w-0 flex-1 truncate text-sm leading-tight',
                          isCollapsed && 'sr-only'
                        )}
                      >
                        {label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}

            {adminModules.length > 0 && (
              <>
                <div className="border-t border-[var(--color-border)] my-1" />
                <div className="space-y-0.5">
                  <div className="flex min-h-[2.25rem] items-center px-3 py-1 text-sm font-semibold uppercase tracking-wider text-[var(--color-secondary-text)]">
                    <span className={cn(isCollapsed && 'invisible')}>
                      {t('navigation.adminModules') || 'Administration'}
                    </span>
                  </div>
                  {hasAdminDashboardPermission && (
                    <Link
                      to="/admin/dashboard"
                      title={
                        isCollapsed
                          ? t('navigation.adminDashboard') || 'Admin Dashboard'
                          : undefined
                      }
                      className={cn(
                        SIDEBAR_NAV_ROW_CLASS,
                        'transition-colors',
                        location.pathname === '/admin/dashboard'
                          ? 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]'
                          : 'text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)]'
                      )}
                    >
                      <CogIcon className={SIDEBAR_MONO_ICON} />
                      <span
                        className={cn(
                          'min-w-0 flex-1 truncate text-sm font-medium leading-tight',
                          isCollapsed && 'sr-only'
                        )}
                      >
                        {t('navigation.adminDashboard') || 'Admin Dashboard'}
                      </span>
                    </Link>
                  )}
                  {adminModules.map((item: any) => {
                    const itemPath = item.route_path || '#';
                    const isActiveModule =
                      location.pathname === itemPath ||
                      location.pathname.startsWith(itemPath + '/');
                    const adminLabel = getTranslatedText(item.display_name, item.translation_key);

                    return (
                      <Link
                        key={item.id}
                        to={itemPath}
                        title={isCollapsed ? adminLabel : undefined}
                        className={cn(
                          SIDEBAR_NAV_ROW_CLASS,
                          'transition-colors',
                          isActiveModule
                            ? 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]'
                            : 'text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)]'
                        )}
                      >
                        {item.icon && iconService.isIconAvailable(item.icon) ? (
                          React.createElement(iconService.getIcon(item.icon), {
                            className: SIDEBAR_MONO_ICON,
                          })
                        ) : (
                          <div className="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center">
                            {getIconComponent(item.icon || 'FolderIcon')}
                          </div>
                        )}
                        <span
                          className={cn(
                            'min-w-0 flex-1 truncate text-sm leading-tight',
                            isCollapsed && 'sr-only'
                          )}
                        >
                          {adminLabel}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Favorites */}
      {favorites && favorites.length > 0 && !isCollapsed && (
        <div className="p-2 border-b border-[var(--color-border)]">
          <h3 className="text-sm font-medium text-[var(--color-secondary-text)] mb-1">
          {t('navigation.favorites')}
          </h3>
          <div className="space-y-1">
            {favorites.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigationClick(item)}
                className={`w-full text-left px-3 py-2 rounded text-sm text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors ${
                  isActive(item.route_path) ? 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-[18px] h-[18px] flex-shrink-0 flex items-center justify-center">{getIconComponent(item.icon)}</div>
                  <span className="truncate text-sm flex-1 min-w-0 leading-tight">
                    {translationsLoading ? item.display_name : (() => {
                      return getTranslatedText(item.display_name, item.translation_key);
                    })()}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent Items */}
      {recentItems && recentItems.length > 0 && !isCollapsed && (
        <div className="p-2 border-b border-[var(--color-border)]">
          <h3 className="text-sm font-medium text-[var(--color-secondary-text)] mb-1">
            {t('navigation.recent')}
          </h3>
          <div className="space-y-1">
            {recentItems.slice(0, 5).map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigationClick(item)}
                className={`w-full text-left px-3 py-2 rounded text-sm text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)] hover:text-[var(--color-secondary-text)] transition-colors ${
                  isActive(item.route_path) ? 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-[18px] h-[18px] flex-shrink-0 flex items-center justify-center">{getIconComponent(item.icon)}</div>
                  <span className="truncate text-sm flex-1 min-w-0 leading-tight">
                    {translationsLoading ? item.display_name : (() => {
                      return getTranslatedText(item.display_name, item.translation_key);
                    })()}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Resources Widget */}
      {!isCollapsed && (
        <div className="p-3 border-b border-[var(--color-border)]">
          <ResourcesWidget 
            isCollapsed={false}
            maxItems={3}
            mode="smart"
          />
        </div>
      )}
      </div>
      {/* End Scrollable Content Area */}

      </aside>
    </>
  );
};
